import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CohortData } from "@/types/cohort.types";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface CohortConversionChartProps {
  cohorts: CohortData[];
  isLoading?: boolean;
}

export const CohortConversionChart = ({ cohorts, isLoading }: CohortConversionChartProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversion by Cohort</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const chartData = cohorts.map(cohort => ({
    name: cohort.cohort,
    conversionRate: cohort.conversionRate,
    conversions: cohort.conversions,
    size: cohort.cohortSize
  })).reverse(); // Reverse to show oldest to newest

  return (
    <Card>
      <CardHeader>
        <CardTitle>📈 Conversion nach Kohorte</CardTitle>
        <CardDescription>
          Wie entwickeln sich die Conversion Rates über verschiedene Kohorten?
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis 
                dataKey="name" 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                label={{ value: 'Conversion Rate (%)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  color: 'hsl(var(--popover-foreground))'
                }}
                formatter={(value: number, name: string) => {
                  if (name === 'conversionRate') return [`${value.toFixed(2)}%`, 'Conversion Rate'];
                  return [value, name];
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="conversionRate" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                name="Conversion Rate (%)"
                dot={{ fill: 'hsl(var(--primary))' }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Keine Kohortendaten verfügbar
          </div>
        )}
      </CardContent>
    </Card>
  );
};
